import type { Slice } from '@tiptap/pm/model';
import type { EditorView } from '@tiptap/pm/view';
import { insertImageFiles } from './insert-image-file';

/**
 * Inserts every dropped image file as a base64 data URL. Without this, the browser falls
 * back to its default drop behaviour and navigates to the image URL. The handler
 * returns false for non-image drops (text/uri/internal node moves) so ProseMirror's
 * own drop logic (link drops, intra-doc moves) keeps working.
 */
export const handleImageDrop = (
  view: EditorView,
  event: DragEvent,
  isEditorAlive: () => boolean,
  _slice: Slice,
  moved: boolean
): boolean => {
  // Internal node-drag: let ProseMirror move the node itself.
  if (moved) {
    return false;
  }

  const files = event.dataTransfer?.files;
  if (!files || files.length === 0) {
    return false;
  }

  const images = Array.from(files).filter((file) => file.type.startsWith('image/'));
  if (images.length === 0) {
    return false;
  }

  event.preventDefault();
  // Resolve the drop point now: handleDrop fires before ProseMirror moves the selection,
  // so without this the images land at wherever the caret happened to be.
  const dropPos = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos;
  void insertImageFiles(view, images, isEditorAlive, dropPos);
  return true;
};
